/**
 * A map inside a result card, with a stated fallback.
 *
 * No pillar hand-assembles a map fence string again: the syntax is a wire
 * format, and eighteen families writing it by hand is eighteen chances to emit
 * a block the shared parser reads differently from the back end.
 *
 * Coordinate pins only, and that is not a style preference. A name-only pin is
 * resolved by `GeoContentRefProvider`, which is mounted in chat and in version
 * content and nowhere else — so off session content a named place has no
 * resolvable ref, no geocode request is ever made, and the block degrades to a
 * list. A coordinate pin skips resolution entirely and always draws.
 *
 * The canvas is forced open: `Cmarkdownmap` refuses to mount its canvas while
 * its shell is compact, so an embedder that does not ask for the expanded
 * surface gets a 317px tile where it wanted a map.
 */
"use client";
import type { CSSProperties, ReactElement } from "react";
import { Cmarkdownmap, MapPinRestatement } from "@/components/ui/Cmarkdownmap";
import { useCresultcardNestedRadius } from "./cresultcard.radius";
import { CRC_TEXT_FAINT, radiusBelow } from "./cresultcard.tokens";

/**
 * What the block is told not to restate.
 *
 * `Cmarkdownmap` names every pin twice — in the glass rail, and as a rounded
 * coordinate trailing that rail's row — which is right for a map standing
 * alone in a deliverable and wrong for one nested under an identity that has
 * already said all of it. The rounded pair goes in every case, because this
 * component prints the full-precision pair itself and two different roundings
 * of one coordinate is the worst state. The rail goes only for a SINGLE pin,
 * where the name is either this block's own chrome title or the identity
 * directly above it; a multi-pin map's rail is a legend, and a legend is not
 * a restatement.
 */
const SINGLE_PIN_SUPPRESSED: readonly MapPinRestatement[] = [
  MapPinRestatement.LocationRail,
  MapPinRestatement.CoordinateDetail,
];
const MULTI_PIN_SUPPRESSED: readonly MapPinRestatement[] = [
  MapPinRestatement.CoordinateDetail,
];
const SINGLE_PIN_COUNT = 1;

/**
 * The map's own furniture, on the kit's ladder.
 *
 * `Cmarkdownmap` is designed as a block standing alone in a deliverable, so its
 * location-rail rows carry a 14px rung — off this kit's ladder, and two rungs
 * above the frame a card nests the canvas in. The rail is a surface INSIDE that
 * frame, so it takes the rung below it, and the variable is the only thing this
 * component has to say to get it: the stylesheet keeps 14px for every embedder
 * that names nothing.
 */
type CresultcardMapFrameStyle = CSSProperties & {
  "--map-row-radius": string;
};
const MAP_RADIUS_UNIT = "px";

const FENCE_TITLE_PREFIX = "title: ";
const FENCE_ZOOM_PREFIX = "zoom: ";
const FENCE_PIN_SEPARATOR = " :: ";
const FENCE_LINE_BREAK = "\n";
const COORDINATE_SEPARATOR = ", ";
/** Six decimals is roughly a tenth of a metre — past that it is noise. */
const COORDINATE_PRECISION = 6;

export interface CresultcardMapPin {
  readonly latitude: number;
  readonly longitude: number;
  /** What the pin IS. It becomes the pin's label in the block's own list. */
  readonly label: string;
  readonly caption?: string;
}

interface CresultcardMapProps {
  readonly pins: readonly [CresultcardMapPin, ...CresultcardMapPin[]];
  /**
   * The block's title line, shown by the map's own chrome. OMITTED when an
   * identity block already sits directly above the canvas — a chrome title
   * under a name that was just read is the same word twice, and until now there
   * was no way to decline it.
   */
  readonly title?: string;
  readonly zoom?: number;
  /** The floor for the drawn canvas. The map owns everything above it. */
  readonly height: number;
  /**
   * The full-precision pair beneath the canvas. It is the map kind's stated
   * fallback, so it is on by default; a holder that prints the pair itself
   * turns it off rather than printing it twice.
   */
  readonly coordinateLine?: boolean;
}

function pinLine(pin: CresultcardMapPin): string {
  const coordinates = `${pin.latitude.toString()}${COORDINATE_SEPARATOR}${pin.longitude.toString()}`;
  const parts = [coordinates, pin.label];
  if (pin.caption != null) {
    parts.push(pin.caption);
  }
  return parts.join(FENCE_PIN_SEPARATOR);
}

/** The coordinate pair, stated in the open. A map that fails still leaves this. */
export function formatCresultcardCoordinates(pin: CresultcardMapPin): string {
  return `${pin.latitude.toFixed(COORDINATE_PRECISION)}${COORDINATE_SEPARATOR}${pin.longitude.toFixed(COORDINATE_PRECISION)}`;
}

export function CresultcardMap({
  pins,
  title,
  zoom,
  height,
  coordinateLine = true,
}: CresultcardMapProps): ReactElement {
  const radius = useCresultcardNestedRadius();
  const directives: string[] = [];
  if (title != null) {
    directives.push(`${FENCE_TITLE_PREFIX}${title}`);
  }
  if (zoom != null) {
    directives.push(`${FENCE_ZOOM_PREFIX}${String(zoom)}`);
  }
  const content = [...directives, ...pins.map(pinLine)].join(FENCE_LINE_BREAK);
  const suppressed =
    pins.length === SINGLE_PIN_COUNT
      ? SINGLE_PIN_SUPPRESSED
      : MULTI_PIN_SUPPRESSED;
  const frameStyle: CresultcardMapFrameStyle = {
    borderRadius: radius,
    overflow: "hidden",
    "--map-row-radius": `${String(radiusBelow(radius))}${MAP_RADIUS_UNIT}`,
  };

  return (
    <div
      data-el="Cresultcard/Map/Root"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 5,
        minWidth: 0,
      }}
    >
      <Cmarkdownmap
        dataElScope="Cresultcard/Map"
        canvasMinHeight={height}
        content={content}
        contentMinHeight={height}
        defaultExpanded
        style={frameStyle}
        suppressedRestatements={suppressed}
      />
      {coordinateLine && (
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 540,
            fontVariantNumeric: "tabular-nums",
            color: CRC_TEXT_FAINT,
          }}
        >
          {formatCresultcardCoordinates(pins[0])}
        </span>
      )}
    </div>
  );
}
